import React, { useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2, ShieldCheck } from "lucide-react";
import {
  BusinessProposalData,
  buildOnboardingBillingDetails,
  formatProposalAmount,
  getPaymentTypeLabel,
} from "../../lib/businessProposalParams";
import {
  BusinessOnboardingCompleteResult,
  CompleteBusinessOnboardingPayload,
  completeBusinessProposalPayment,
  createBusinessProposalSetupIntent,
  getBusinessProposalErrorMessage,
} from "../../services/businessProposal";

interface BusinessProposalPaymentFormProps {
  proposal: BusinessProposalData;
  onSuccess: (result?: BusinessOnboardingCompleteResult) => void;
  onBack: () => void;
  isDemo?: boolean;
}

const cardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: "15px",
      color: "#1f2937",
      "::placeholder": { color: "#9ca3af" },
    },
    invalid: { color: "#dc2626" },
  },
};

const BusinessProposalPaymentForm: React.FC<BusinessProposalPaymentFormProps> = ({
  proposal,
  onSuccess,
  onBack,
  isDemo = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const currencySymbol = proposal.currencySymbol || "£";
  const billingType = proposal.paymentType === "yearly" ? "yearly" : "monthly";
  const billingDetails = buildOnboardingBillingDetails(proposal);

  const handlePay = async () => {
    if (!stripe || !elements) {
      setError("Stripe is not ready yet. Please wait a moment and try again.");
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      setError("Card details are required.");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      if (isDemo) {
        const { error: pmError } = await stripe.createPaymentMethod({
          type: "card",
          card,
          billing_details: billingDetails,
        });

        if (pmError) {
          throw new Error(pmError.message || "Please check your card details.");
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
        onSuccess();
        return;
      }

      const proposalId = proposal.id;
      if (!proposalId) {
        throw new Error("Onboarding ID is missing from this link.");
      }

      const setupIntent = await createBusinessProposalSetupIntent(proposalId);
      const clientSecret = setupIntent?.client_secret;
      if (!clientSecret) {
        throw new Error("Unable to start secure payment. Please contact Glossa support.");
      }

      const confirmed = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card,
          billing_details: billingDetails,
        },
      });

      if (confirmed.error) {
        throw new Error(confirmed.error.message || "Card verification failed.");
      }

      const paymentMethod = confirmed.setupIntent?.payment_method;
      if (!paymentMethod || typeof paymentMethod !== "string") {
        throw new Error("Could not save payment method.");
      }

      const completePayload: CompleteBusinessOnboardingPayload = {
        payment_method: paymentMethod,
        plan_id: proposal.planId,
        type: billingType,
        stripe_email: proposal.customerEmail,
        customer_name: proposal.customerName || proposal.businessName,
        billing_country: "GB",
        accepted_terms: true,
        accepted_privacy: true,
      };

      const result = await completeBusinessProposalPayment(proposalId, completePayload);

      onSuccess(result);
    } catch (err: unknown) {
      setError(getBusinessProposalErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium text-[#885ABB] hover:text-[#7647a8] transition-colors"
        >
          <span className="text-xl">&larr;</span> Back
        </button>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-[#402B69]">
          Complete your subscription
        </h2>
        <p className="mt-2 text-gray-600 leading-relaxed">
          Enter your UK card details below to activate your Glossa subscription. Your first
          payment will be taken now and your card will be saved securely for recurring billing
          via Stripe
          {isDemo ? " (demo preview — no charge will be made)" : ""}.
        </p>
      </div>

      <div className="rounded-xl border border-[#E5E0EB] bg-[#FAF8FD] p-4 sm:p-5 space-y-3">
        <div className="flex justify-between gap-4 text-sm">
          <span className="text-gray-500">Package</span>
          <span className="font-semibold text-[#402B69] text-right">{proposal.packageName}</span>
        </div>
        <div className="flex justify-between gap-4 text-sm">
          <span className="text-gray-500">Billing</span>
          <span className="font-semibold text-[#402B69]">
            {getPaymentTypeLabel(proposal.paymentType)}
          </span>
        </div>
        <div className="flex justify-between gap-4 text-sm">
          <span className="text-gray-500">Billing country</span>
          <span className="font-semibold text-[#402B69]">United Kingdom</span>
        </div>
        <div className="flex justify-between gap-4 text-sm border-t border-[#E5E0EB] pt-3">
          <span className="text-gray-500">Amount due</span>
          <span className="text-lg font-bold text-[#885ABB]">
            {formatProposalAmount(proposal.amount, currencySymbol, proposal.paymentType)}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-semibold text-gray-700">Card details</label>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <CardElement options={cardElementOptions} />
        </div>
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <ShieldCheck className="w-4 h-4 text-[#885ABB] shrink-0 mt-0.5" />
          <span>
            Secured by Stripe. Glossa does not store your full card number.
            {isDemo ? " Use test card 4242 4242 4242 4242 in demo mode." : ""}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={handlePay}
        disabled={processing || !stripe}
        className="w-full h-12 sm:h-14 bg-[#885ABB] hover:bg-[#7647a8] text-white rounded-xl font-bold text-sm sm:text-base transition-all disabled:opacity-60 shadow-lg shadow-[#885ABB]/20 flex items-center justify-center gap-2"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing payment...
          </>
        ) : (
          `Pay ${formatProposalAmount(proposal.amount, currencySymbol, proposal.paymentType)}`
        )}
      </button>
    </div>
  );
};

export default BusinessProposalPaymentForm;
